import React from 'react';
import { createContext, useContext } from 'react';
import { usePrincipal } from '@/core/auth/appToken';
import type { OrganizationType } from '@/models/organizationType';

interface TenantInfo {
  /** Active organization id from the backend principal (E01 §5). */
  readonly organizationId: string;
  /** Organisation type from the backend principal. */
  readonly organizationType: OrganizationType;
}

const TenantContext = createContext<TenantInfo | null>(null);

interface TenantContextProviderProps {
  readonly children: React.ReactNode;
}

/**
 * Exposes the active organization from the backend-issued principal (E01 §5/§6) via context —
 * for display/labeling only; the backend derives the tenant from the token, never send tenantId.
 */
export function TenantContextProvider({ children }: TenantContextProviderProps) {
  const principal = usePrincipal();

  const tenantInfo: TenantInfo | null =
    principal === null
      ? null
      : {
          organizationId: principal.organizationId,
          organizationType: principal.organizationType as OrganizationType,
        };

  return <TenantContext.Provider value={tenantInfo}>{children}</TenantContext.Provider>;
}

/** Returns current tenant info from the backend principal, or null if not resolved yet. */
export function useTenant(): TenantInfo | null {
  return useContext(TenantContext);
}
